import axios from "axios";
import mammoth from "mammoth";
import { marked } from "marked";
import * as XLSX from "xlsx";
import { BASE_URL } from "./baseUrl";
import { getClientSource } from "./Functions";

const MAX_PREVIEW_ROWS = 500;
const MAX_PREVIEW_COLS = 40;
export const MAX_PROJECT_DOC_UPLOAD_FILES = 30;

export const PDF_MIME = "application/pdf";
export const TEXT_PLAIN_MIME = "text/plain";
export const JSON_MIME = "application/json";
export const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const MD_MIME = "text/markdown";
export const RTF_MIME = "application/rtf";
export const TEXT_RTF_MIME = "text/rtf";
export const X_RTF_MIME = "application/x-rtf";
export const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MIME = "application/vnd.ms-excel";
export const HTML_MIME = "text/html";

const EXCEL_MIMES = new Set([XLSX_MIME, XLS_MIME]);

const extFromName = (name) => {
  const m = String(name || "").match(/\.([a-z0-9]+)$/i);
  return m ? `.${m[1].toLowerCase()}` : "";
};

const isEnvFileName = (fileName) => {
  const base = String(fileName || "")
    .trim()
    .split(/[/\\]/)
    .pop()
    .toLowerCase();
  return base === ".env" || base.endsWith(".env");
};

const isTxtFileName = (fileName) => extFromName(fileName) === ".txt";
const isJsonFileName = (fileName) => extFromName(fileName) === ".json";
const isHtmlFileName = (fileName) => {
  const ext = extFromName(fileName);
  return ext === ".html" || ext === ".htm";
};

export const isProjectDocHtml = ({ mimeType, fileName } = {}) => {
  if (String(mimeType || "").toLowerCase() === HTML_MIME) return true;
  return isHtmlFileName(fileName);
};

export const isProjectDocJson = ({ mimeType, fileName } = {}) => {
  const mime = String(mimeType || "").toLowerCase();
  if (mime === JSON_MIME) return true;
  return isJsonFileName(fileName);
};

export const isProjectDocPlainText = ({ mimeType, fileName } = {}) => {
  if (isProjectDocJson({ mimeType, fileName })) return false;
  const mime = String(mimeType || "").toLowerCase();
  const ext = extFromName(fileName);
  if (mime === MD_MIME || ext === ".md" || ext === ".markdown") return false;
  if (mime === TEXT_PLAIN_MIME) return true;
  return isTxtFileName(fileName) || isEnvFileName(fileName);
};

export const isProjectDocTextPreview = (doc) =>
  isProjectDocPlainText(doc) || isProjectDocJson(doc);

export const isProjectDocEnv = ({ fileName } = {}) => isEnvFileName(fileName);

export const isProjectDocPdf = ({ mimeType, fileName } = {}) => {
  if (String(mimeType || "").toLowerCase() === PDF_MIME) return true;
  const ext = extFromName(fileName);
  return ext === ".pdf";
};

export const isProjectDocDocx = ({ mimeType, fileName } = {}) => {
  if (String(mimeType || "").toLowerCase() === DOCX_MIME) return true;
  return extFromName(fileName) === ".docx";
};

export const isProjectDocMarkdown = ({ mimeType, fileName } = {}) => {
  const mime = String(mimeType || "").toLowerCase();
  if (mime === MD_MIME) return true;
  const ext = extFromName(fileName);
  return ext === ".md" || ext === ".markdown";
};

export const isProjectDocRtf = ({ mimeType, fileName } = {}) => {
  const mime = String(mimeType || "").toLowerCase();
  if (mime === RTF_MIME || mime === TEXT_RTF_MIME || mime === X_RTF_MIME) return true;
  return extFromName(fileName) === ".rtf";
};

export const isProjectDocExcel = ({ mimeType, fileName } = {}) => {
  const mime = String(mimeType || "").toLowerCase();
  if (EXCEL_MIMES.has(mime)) return true;
  const ext = extFromName(fileName);
  return ext === ".xlsx" || ext === ".xls";
};

export const getProjectDocUploadTagLabel = (doc) => {
  if (!doc?.hasFile) return "Written doc";
  if (doc?.hasMultipleFiles || Number(doc?.fileCount) > 1) {
    return `${doc.fileCount || doc.files?.length || 0} files`;
  }
  if (isProjectDocExcel(doc)) return "Excel sheet";
  if (isProjectDocDocx(doc)) return "Word doc";
  if (isProjectDocRtf(doc)) return "RTF document";
  if (isProjectDocMarkdown(doc)) return "Markdown";
  if (isProjectDocHtml(doc)) return "HTML file";
  if (isProjectDocJson(doc)) return "JSON file";
  if (isProjectDocPlainText(doc)) return isProjectDocEnv(doc) ? ".env file" : "Text file";
  if (isProjectDocPdf(doc)) return "PDF upload";
  return "File upload";
};

export const getProjectDocDownloadExtension = (doc) => {
  if (isProjectDocExcel(doc)) {
    const ext = extFromName(doc?.fileName);
    if (ext === ".xls") return ".xls";
    return ".xlsx";
  }
  if (isProjectDocDocx(doc)) return ".docx";
  if (isProjectDocRtf(doc)) return ".rtf";
  if (isProjectDocMarkdown(doc)) {
    const ext = extFromName(doc?.fileName);
    return ext === ".markdown" ? ".markdown" : ".md";
  }
  if (isProjectDocHtml(doc)) {
    return extFromName(doc?.fileName) === ".htm" ? ".htm" : ".html";
  }
  if (isProjectDocPlainText(doc)) {
    if (isEnvFileName(doc?.fileName)) return ".env";
    return ".txt";
  }
  if (isProjectDocJson(doc)) return ".json";
  return ".pdf";
};

export const normalizeProjectDocFileName = (fileName) => {
  const base = String(fileName || "")
    .trim()
    .split(/[/\\]/)
    .pop();
  return base.toLowerCase();
};

export const findProjectDocFileByName = (files, uploadFileName) => {
  const target = normalizeProjectDocFileName(uploadFileName);
  if (!target || !Array.isArray(files)) return null;
  return files.find((file) => normalizeProjectDocFileName(file?.fileName) === target) || null;
};

export const getProjectDocReplaceUploadMode = (doc) => {
  if (doc?.hasMultipleFiles && Array.isArray(doc?.files) && doc.files.length) {
    const allExcel = doc.files.every((file) => isProjectDocExcel(file));
    if (allExcel) return "excel";
    return "upload";
  }
  if (isProjectDocExcel(doc)) return "excel";
  return "upload";
};

export const getProjectDocReplaceAccept = (doc) => {
  const mode = getProjectDocReplaceUploadMode(doc);
  if (mode === "excel") return EXCEL_ACCEPT;
  if (doc?.hasMultipleFiles && Array.isArray(doc?.files) && doc.files.length) {
    const hasExcel = doc.files.some((file) => isProjectDocExcel(file));
    const hasOther = doc.files.some((file) => !isProjectDocExcel(file));
    if (hasExcel && hasOther) {
      return `${PROJECT_DOC_UPLOAD_ACCEPT},${EXCEL_ACCEPT}`;
    }
  }
  return PROJECT_DOC_UPLOAD_ACCEPT;
};

const validatePdfBuffer = (buf) => {
  if (buf.byteLength < 4) return;
  const u = new Uint8Array(buf);
  const looksPdf = u[0] === 0x25 && u[1] === 0x50 && u[2] === 0x44 && u[3] === 0x46;
  if (!looksPdf) {
    throw new Error("Unexpected response when loading the document.");
  }
};

/**
 * Loads a project documentation uploaded file through the API.
 */
export async function fetchProjectDocumentationFileBlob(documentId, fileDocumentId) {
  const accessToken = localStorage.getItem("accessToken");
  const parentId = Number(documentId);
  const fileId = fileDocumentId != null ? Number(fileDocumentId) : parentId;
  const params =
    Number.isFinite(fileId) && fileId > 0 && fileId !== parentId ? { fileDocumentId: fileId } : undefined;

  const res = await axios.get(`${BASE_URL}/api/project-documentation/${parentId}/file`, {
    responseType: "arraybuffer",
    params,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      company_source: getClientSource(),
    },
    validateStatus: () => true,
  });

  if (res.status === 401) {
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (res.status !== 200) {
    throw new Error("Could not load the document file.");
  }

  const contentType = String(res.headers?.["content-type"] || "").split(";")[0].trim().toLowerCase();
  const mime = contentType || PDF_MIME;

  if (mime === PDF_MIME) {
    validatePdfBuffer(res.data);
  }

  return new Blob([res.data], { type: mime });
}

/** @deprecated Use fetchProjectDocumentationFileBlob */
export const fetchProjectDocumentationPdfBlob = fetchProjectDocumentationFileBlob;

export const PDF_ACCEPT = "application/pdf,.pdf";
export const DOCX_ACCEPT =
  ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const MARKDOWN_ACCEPT = ".md,.markdown,text/markdown";
export const RTF_ACCEPT = ".rtf,application/rtf,text/rtf,application/x-rtf";
export const TEXT_ACCEPT = ".txt,.env,text/plain";
export const JSON_ACCEPT = ".json,application/json";
export const HTML_ACCEPT = ".html,.htm,text/html";
export const EXCEL_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

export const PROJECT_DOC_UPLOAD_ACCEPT = `${PDF_ACCEPT},${DOCX_ACCEPT},${RTF_ACCEPT},${MARKDOWN_ACCEPT},${TEXT_ACCEPT},${JSON_ACCEPT},${HTML_ACCEPT}`;

const isAllowedDocUploadFile = (file) => {
  const name = String(file.name || "").toLowerCase();
  if (file.type === PDF_MIME || name.endsWith(".pdf")) return true;
  if (file.type === DOCX_MIME || name.endsWith(".docx")) return true;
  if (
    file.type === RTF_MIME ||
    file.type === TEXT_RTF_MIME ||
    file.type === X_RTF_MIME ||
    name.endsWith(".rtf")
  ) {
    return true;
  }
  if (file.type === MD_MIME || name.endsWith(".md") || name.endsWith(".markdown")) return true;
  if (isJsonFileName(file.name) || file.type === JSON_MIME) return true;
  if (isHtmlFileName(file.name) || file.type === HTML_MIME) return true;
  if (isTxtFileName(file.name) || isEnvFileName(file.name)) return true;
  if (file.type === TEXT_PLAIN_MIME) return isTxtFileName(file.name) || isEnvFileName(file.name);
  return false;
};

export const validatePickedUploadFiles = (files, mode) => {
  const list = Array.from(files || []).filter(Boolean);
  if (!list.length) {
    return { ok: false, message: "Choose at least one file (max 15MB each)." };
  }
  if (list.length > MAX_PROJECT_DOC_UPLOAD_FILES) {
    return {
      ok: false,
      message: `You can upload up to ${MAX_PROJECT_DOC_UPLOAD_FILES} files at once.`,
    };
  }
  for (const file of list) {
    const check = validatePickedUploadFile(file, mode);
    if (!check.ok) return check;
  }
  return { ok: true, files: list };
};

export const validatePickedUploadFile = (file, mode) => {
  if (!file) return { ok: false, message: "Choose a file (max 15MB)." };
  const max = 15 * 1024 * 1024;
  if (file.size > max) {
    return { ok: false, message: "File is too large (max 15MB)." };
  }
  const name = String(file.name || "").toLowerCase();
  if (mode === "pdf") {
    if (file.type === PDF_MIME || name.endsWith(".pdf")) return { ok: true };
    return { ok: false, message: "Please choose a PDF file." };
  }
  if (mode === "excel") {
    if (EXCEL_MIMES.has(file.type) || name.endsWith(".xlsx") || name.endsWith(".xls")) {
      return { ok: true };
    }
    return { ok: false, message: "Please choose an Excel file (.xlsx or .xls)." };
  }
  if (mode === "upload") {
    if (isAllowedDocUploadFile(file)) return { ok: true };
    return {
      ok: false,
      message: "Only PDF, Word (.docx), RTF (.rtf), Markdown (.md), plain text (.txt, .env), JSON (.json), or HTML (.html, .htm) files are allowed.",
    };
  }
  return { ok: false, message: "Invalid upload mode." };
};

const normalizePreviewRows = (rows, colCount) =>
  rows.map((row) => {
    const arr = Array.isArray(row) ? [...row] : [];
    while (arr.length < colCount) arr.push("");
    return arr.slice(0, colCount).map((cell) => (cell == null ? "" : String(cell)));
  });

marked.setOptions({ gfm: true, breaks: true });

const MERMAID_BLOCK_RE = /```mermaid[^\n]*\n([\s\S]*?)```/gi;
const MERMAID_START_RE =
  /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|journey|gantt|pie|gitGraph|mindmap|timeline|quadrantChart|requirementDiagram|C4Context|C4Container|C4Component|C4Dynamic|sankey-beta|xychart-beta|block-beta)\b/i;

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderMermaidPreviewBlock = (source) =>
  `<div class="project-doc-mermaid-wrap"><pre class="mermaid">${escapeHtml(source.trim())}</pre></div>`;

export async function parseTextBlobForPreview(blob, { formatJson = false } = {}) {
  const text = await blob.text();
  if (!formatJson) return text;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/**
 * Renders a Markdown file blob as HTML for in-app preview, including Mermaid diagrams.
 */
export async function parseMarkdownBlobForPreview(blob) {
  const text = await blob.text();
  const trimmed = text.trim();
  if (MERMAID_START_RE.test(trimmed) && !trimmed.includes("```")) {
    return renderMermaidPreviewBlock(trimmed);
  }

  const mermaidBlocks = [];
  const markdownWithPlaceholders = text.replace(MERMAID_BLOCK_RE, (_match, diagram) => {
    const index = mermaidBlocks.push(diagram) - 1;
    return `\n\nPROJECT_DOC_MERMAID_${index}\n\n`;
  });

  let html = marked.parse(markdownWithPlaceholders);
  mermaidBlocks.forEach((diagram, index) => {
    const placeholder = `PROJECT_DOC_MERMAID_${index}`;
    const rendered = renderMermaidPreviewBlock(diagram);
    html = html
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, "g"), rendered)
      .replace(new RegExp(placeholder, "g"), rendered);
  });
  return html;
}

/**
 * Converts a Word (.docx) blob to HTML for in-app preview.
 */
export async function parseDocxBlobForPreview(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return {
    html: result.value,
    messages: result.messages || [],
  };
}

/**
 * Parses an Excel blob into sheet tables for in-app preview (read-only).
 */
export async function parseExcelBlobForPreview(blob) {
  const buffer = await blob.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  return workbook.SheetNames.map((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const ref = sheet?.["!ref"];
    if (!ref) {
      return { sheetName, rows: [], colCount: 0, truncated: false };
    }

    const range = XLSX.utils.decode_range(ref);
    const totalRows = range.e.r - range.s.r + 1;
    const totalCols = range.e.c - range.s.c + 1;
    const truncated = totalRows > MAX_PREVIEW_ROWS || totalCols > MAX_PREVIEW_COLS;
    const previewRange = truncated
      ? {
        s: { r: range.s.r, c: range.s.c },
        e: {
          r: Math.min(range.e.r, range.s.r + MAX_PREVIEW_ROWS - 1),
          c: Math.min(range.e.c, range.s.c + MAX_PREVIEW_COLS - 1),
        },
      }
      : ref;

    const rawRows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
      range: previewRange,
    });

    const colCount = Math.min(totalCols, MAX_PREVIEW_COLS);
    const rows = normalizePreviewRows(rawRows, colCount);

    return { sheetName, rows, colCount, truncated };
  });
};

export const buildExcelPreviewTableColumns = (colCount) =>
  Array.from({ length: colCount }, (_, index) => ({
    title: `Col ${index + 1}`,
    dataIndex: String(index),
    key: String(index),
    ellipsis: true,
    width: 120,
  }));

export const buildExcelPreviewTableData = (rows) =>
  rows.map((row, rowIndex) => {
    const record = { key: rowIndex };
    row.forEach((cell, colIndex) => {
      record[String(colIndex)] = cell;
    });
    return record;
  });
